// Using standard Tailwind colors. Adjust if you have custom theme colors.
const colorMap = {
    '0': 'text-black',         // Black (might be hard to see on dark bg)
    '1': 'text-blue-800',      // Dark Blue
    '2': 'text-green-600',     // Dark Green
    '3': 'text-cyan-600',      // Dark Aqua
    '4': 'text-red-600',       // Dark Red
    '5': 'text-purple-600',    // Dark Purple
    '6': 'text-yellow-600',    // Gold
    '7': 'text-gray-400',      // Gray
    '8': 'text-gray-500',      // Dark Gray
    '9': 'text-blue-400',      // Blue
    'a': 'text-green-400',     // Green
    'b': 'text-cyan-400',      // Aqua
    'c': 'text-red-400',       // Red
    'd': 'text-pink-400',      // Light Purple / Pink
    'e': 'text-yellow-400',    // Yellow
    'f': 'text-white',         // White
  };

  const formatMap = {
    'k': 'minecraft-obfuscated', // Requires custom CSS/JS - we'll add a placeholder class
    'l': 'font-bold',            // Bold
    'm': 'line-through',         // Strikethrough
    'n': 'underline',            // Underline
    'o': 'italic',               // Italic
    'r': 'reset',                // Reset all formatting
  };

  /**
   * Parses a Minecraft MOTD string with formatting codes into an array of React elements.
   * @param {string} motdString The MOTD string containing § codes.
   * @param {string} [baseColorClass=colorMap['f']] Optional base Tailwind CSS class for uncolored text if codes are present.
   * @returns {React.ReactNode[]} An array of React nodes (spans with styles or raw string).
   */
  export const parseMinecraftFormatting = (motdString, baseColorClassInput = colorMap['f']) => {
    if (!motdString || typeof motdString !== 'string') {
      return [motdString]; // Return original if invalid (e.g., null, undefined)
    }

    // If no Minecraft codes are present, return the original string.
    // The parent component's styling will then apply.
    if (!/[§&][0-9a-fk-or]/g.test(motdString)) {
      return [motdString];
    }

    const parts = [];
    let currentText = '';
    // Use the provided baseColorClassInput as the initial and reset color.
    let currentColorClass = baseColorClassInput;
    let currentFormatClasses = new Set();

    const resetFormatting = () => {
      currentColorClass = baseColorClassInput; // Reset to the provided base color
      currentFormatClasses.clear();
    };

    // Split by formatting codes, keeping the codes
    const segments = motdString.split(/([§&][0-9a-fk-or])/g);

    segments.forEach((segment) => {
      if (segment.startsWith('§') || segment.startsWith("&")) {
        // Apply previous text segment if any
        if (currentText) {
          parts.push(
            <span key={parts.length} className={`${currentColorClass} ${Array.from(currentFormatClasses).join(' ')}`.trim()}>
              {currentText}
            </span>
          );
          currentText = '';
        }

        // Handle the formatting code
        const code = segment[1];
        if (colorMap[code]) { // Is a Minecraft color code
          currentFormatClasses.clear(); // Minecraft resets formats on color change
          currentColorClass = colorMap[code]; // Apply Minecraft color
        } else if (formatMap[code]) { // Is a Minecraft format code
          if (code === 'r') { // Reset
            resetFormatting();
          } else { // Other formats (bold, italic, etc.)
            currentFormatClasses.add(formatMap[code]);
          }
        }
        // Ignore invalid codes silently
      } else {
        // Append text segment
        currentText += segment;
      }
    });

    // Add any remaining text segment
    if (currentText) {
      parts.push(
        <span key={parts.length} className={`${currentColorClass} ${Array.from(currentFormatClasses).join(' ')}`.trim()}>
          {currentText}
        </span>
      );
    }
    // If motdString consisted only of formatting codes that result in no visible text (e.g., "§a§r"),
    // parts might be empty. Returning an empty array is fine for React.
    return parts;
  };

  // Helper for obfuscated text (Optional - basic CSS version)
  // You might need more complex JS for true random character swapping.
  /* Add to your styles.css if you want basic obfuscation:
  @keyframes obfuscate {
    0% { content: attr(data-text); }
    1% { content: "*"; } // Use a character or random generator
    // ... more steps for randomness ...
    100% { content: attr(data-text); } // Ensures original text is accessible
  }

  .minecraft-obfuscated::before {
    content: attr(data-text); // Set initial content
    animation: obfuscate 1s steps(1, end) infinite;
  }
  .minecraft-obfuscated {
      // Store original text for the ::before pseudo-element
      // You might need JS to set the data-text attribute dynamically
  }
  */